'use client';

import { useState, useEffect, useCallback } from 'react';
import { usePersistedState, usePersistedSet } from '@/lib/usePersistedState';
import { ChevronRight, ChevronDown, FileText, Folder, FolderOpen, RefreshCw, Zap, Shield, BookOpen, ScrollText, ArrowLeft, Flame, Snowflake, Map, Play, ArrowDownToLine } from 'lucide-react';
import { MarkdownBlock } from '@/components/chat/Markdown';

import { soma as somaApi, sessionApi, gateway, chat as chatApi } from '@/lib/api';

// --- Types ---

type TreeNode = {
  name: string;
  path: string;
  type: 'file' | 'dir';
  children?: TreeNode[];
};

type FileData = {
  path: string;
  content: string;
  size: number;
  modified: string;
};

// --- Soma Live Data (from /soma/* endpoints) ---

interface SomaProtocol {
  name: string;
  heat: number;
  status?: string;
  description?: string;
  tags?: string[];
}

interface SomaMuscle {
  name: string;
  heat: number;
  status?: string;
  description?: string;
  tags?: string[];
}

interface SomaPreload {
  path: string;
  name: string;
  stale?: boolean;
  ageDays?: number;
}

interface SomaMap {
  name: string;
  path: string;
  status?: string;
}

interface SomaLiveData {
  protocols: SomaProtocol[];
  muscles: SomaMuscle[];
  preloads: SomaPreload[];
  currentPreload: { path: string; stale: boolean } | null;
  maps: SomaMap[];
  identity: {
    somaPath: string;
    chain: string[];
    hasIdentity: boolean;
    settings: {
      breathe?: { auto: boolean; triggerAt: number; rotateAt: number };
      heat?: { autoDetect: boolean; pinBump: number };
      context?: { notifyAt: number; warnAt: number; urgentAt: number };
      keepalive?: { enabled: boolean };
    };
  } | null;
  doctor?: {
    healthy: boolean;
    checks: { name: string; status: 'ok' | 'warn' | 'error'; detail: string }[];
  } | null;
}

function useSomaLiveData(): { data: SomaLiveData | null; refresh: () => void; loading: boolean } {
  const [data, setData] = useState<SomaLiveData | null>(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [protoRes, muscleRes, preloadRes, mapRes, idRes, doctorRes] = await Promise.all([
        somaApi.protocols().catch(() => null),
        somaApi.muscles().catch(() => null),
        somaApi.preloads().catch(() => null),
        somaApi.maps().catch(() => null),
        somaApi.identity().catch(() => null),
        somaApi.doctor().catch(() => null),
      ]);

      setData({
        protocols: protoRes?.protocols || [],
        muscles: muscleRes?.muscles || [],
        preloads: preloadRes?.preloads || [],
        currentPreload: preloadRes?.current || null,
        maps: mapRes?.maps || [],
        identity: idRes,
        doctor: doctorRes,
      });
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return { data, refresh, loading };
}

async function somaAction(endpoint: string, body?: object): Promise<any> {
  // Soma commands go through chat (session.prompt) — same as typing in the chat.
  // SDK builtins (compact, reload) go through direct IPC.
  if (endpoint === '/soma/pin' && body && 'name' in body) { chatApi.send({ message: `/pin ${(body as any).name}` }); return { ok: true }; }
  if (endpoint === '/soma/kill' && body && 'name' in body) { chatApi.send({ message: `/kill ${(body as any).name}` }); return { ok: true }; }
  if (endpoint === '/soma/inhale') { chatApi.send({ message: `/inhale ${(body as any)?.name || ''}`.trim() }); return { ok: true }; }
  if (endpoint === '/soma/exhale') { chatApi.send({ message: '/exhale' }); return { ok: true }; }
  if (endpoint === '/soma/compact') return sessionApi.compact();
  if (endpoint === '/soma/reload') return sessionApi.reload();
  // Fallback to raw gateway fetch (browser only)
  const gwUrl = typeof window === 'undefined'
    ? (process.env.NEXT_PUBLIC_GATEWAY_URL || 'http://localhost:18790')
    : '/api/gw';
  const res = await fetch(`${gwUrl}${endpoint}`, {
    method: 'POST',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  return res.json();
}

// --- Heat Bar Component ---

function HeatBar({ heat, name, onPin, onKill }: { heat: number; name: string; onPin: () => void; onKill: () => void }) {
  const maxHeat = 10;
  const pct = Math.min(100, (heat / maxHeat) * 100);
  const color = heat >= 8 ? 'bg-red-500' : heat >= 5 ? 'bg-amber-500' : heat >= 3 ? 'bg-cyan-500' : heat > 0 ? 'bg-gray-500' : 'bg-gray-800';
  const label = heat >= 8 ? 'HOT' : heat >= 3 ? 'WARM' : heat > 0 ? 'COOL' : 'COLD';
  const labelColor = heat >= 8 ? 'text-red-400' : heat >= 3 ? 'text-amber-400' : heat > 0 ? 'text-gray-400' : 'text-gray-600';

  return (
    <div className="flex items-center gap-2 group">
      <div className="w-20 h-1.5 rounded-full bg-gray-800 overflow-hidden" title={`Heat: ${heat}`}>
        <div className={`h-full rounded-full ${color} transition-all`} style={{ width: `${pct}%` }} />
      </div>
      <span className={`text-[9px] font-bold uppercase tracking-wider w-8 ${labelColor}`}>{label}</span>
      <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
        <button onClick={onPin} className="p-0.5 rounded hover:bg-orange-500/20 text-gray-600 hover:text-orange-400 transition-colors" title={`Pin ${name} (+5 heat)`}>
          <Flame className="h-3 w-3" />
        </button>
        <button onClick={onKill} className="p-0.5 rounded hover:bg-blue-500/20 text-gray-600 hover:text-blue-400 transition-colors" title={`Kill ${name} (heat → 0)`}>
          <Snowflake className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}

// --- Tree Component ---

function TreeItem({
  node,
  depth,
  selectedPath,
  onSelect,
  expandedDirs,
  onToggleDir,
}: {
  node: TreeNode;
  depth: number;
  selectedPath: string | null;
  onSelect: (path: string) => void;
  expandedDirs: Set<string>;
  onToggleDir: (path: string) => void;
}) {
  const isExpanded = expandedDirs.has(node.path);
  const isSelected = node.path === selectedPath;
  const isDir = node.type === 'dir';

  return (
    <div>
      <button
        onClick={() => isDir ? onToggleDir(node.path) : onSelect(node.path)}
        className={`
          w-full flex items-center gap-1.5 px-2 py-1 text-left text-[13px] rounded transition-colors
          ${isSelected ? 'bg-cyan-500/15 text-cyan-400' : 'text-gray-400 hover:bg-white/5 hover:text-gray-200'}
        `}
        style={{ paddingLeft: `${depth * 16 + 8}px` }}
      >
        {isDir ? (
          <>
            {isExpanded ? <ChevronDown className="h-3 w-3 shrink-0 text-gray-500" /> : <ChevronRight className="h-3 w-3 shrink-0 text-gray-500" />}
            {isExpanded ? <FolderOpen className="h-3.5 w-3.5 shrink-0 text-cyan-500/70" /> : <Folder className="h-3.5 w-3.5 shrink-0 text-gray-500" />}
          </>
        ) : (
          <>
            <span className="w-3" />
            <FileText className="h-3.5 w-3.5 shrink-0 text-gray-600" />
          </>
        )}
        <span className="truncate">{node.name}</span>
      </button>
      {isDir && isExpanded && node.children?.map((child) => (
        <TreeItem
          key={child.path}
          node={child}
          depth={depth + 1}
          selectedPath={selectedPath}
          onSelect={onSelect}
          expandedDirs={expandedDirs}
          onToggleDir={onToggleDir}
        />
      ))}
    </div>
  );
}

// --- Frontmatter Parser ---

function parseFrontmatter(content: string): { meta: Record<string, string>; body: string } {
  const match = content.match(/^---\n([\s\S]*?)\n---\n([\s\S]*)$/);
  if (!match) return { meta: {}, body: content };

  const meta: Record<string, string> = {};
  for (const line of match[1].split('\n')) {
    const idx = line.indexOf(':');
    if (idx > 0) {
      meta[line.slice(0, idx).trim()] = line.slice(idx + 1).trim();
    }
  }
  return { meta, body: match[2] };
}

// --- Category badges ---

function getCategoryColor(path: string): string {
  if (path.startsWith('body/')) return 'text-purple-400 bg-purple-400/10';
  if (path.startsWith('amps/muscles/')) return 'text-orange-400 bg-orange-400/10';
  if (path.startsWith('amps/protocols/')) return 'text-blue-400 bg-blue-400/10';
  if (path.startsWith('memory/sessions/')) return 'text-green-400 bg-green-400/10';
  if (path.startsWith('memory/preloads/')) return 'text-yellow-400 bg-yellow-400/10';
  return 'text-gray-400 bg-gray-400/10';
}

function getCategoryLabel(path: string): string {
  if (path.startsWith('body/')) return 'BODY';
  if (path.startsWith('amps/muscles/')) return 'MUSCLE';
  if (path.startsWith('amps/protocols/')) return 'PROTOCOL';
  if (path.startsWith('memory/sessions/')) return 'SESSION';
  if (path.startsWith('memory/preloads/')) return 'PRELOAD';
  return 'FILE';
}



// --- Dashboard Overview ---

type DashboardData = {
  soul: string;
  stats: { muscles: number; protocols: number; sessions: number; preloads: number };
  recentSessions: { path: string; name: string; date: string; firstLine: string }[];
  promptSize: { length: number; estimatedTokens: number };
};

function useDashboardData(tree: TreeNode[]) {
  const [data, setData] = useState<DashboardData | null>(null);

  useEffect(() => {
    if (tree.length === 0) return;

    async function load() {
      // Count files by category
      function countFiles(nodes: TreeNode[], prefix: string): number {
        let count = 0;
        for (const n of nodes) {
          if (n.type === 'file' && n.path.startsWith(prefix) && !n.name.startsWith('_') && !n.name.startsWith('scratchpad')) count++;
          if (n.children) count += countFiles(n.children, prefix);
        }
        return count;
      }

      const stats = {
        muscles: countFiles(tree, 'amps/muscles/'),
        protocols: countFiles(tree, 'amps/protocols/'),
        sessions: countFiles(tree, 'memory/sessions/'),
        preloads: countFiles(tree, 'memory/preloads/'),
      };

      // Fetch soul
      let soul = '';
      try {
        const d = await gateway.brain.file('body/soul.md');
        const { body } = parseFrontmatter(d.content);
        soul = body.trim();
      } catch { /* */ }

      // Fetch recent sessions (get session file names from tree)
      const sessionDir = tree.find(n => n.name === 'memory')?.children?.find(n => n.name === 'sessions');
      const sessionFiles = (sessionDir?.children || [])
        .filter(n => n.type === 'file' && !n.name.startsWith('scratchpad'))
        .sort((a, b) => b.name.localeCompare(a.name))
        .slice(0, 5);

      const recentSessions: DashboardData['recentSessions'] = [];
      for (const sf of sessionFiles) {
        try {
          const d = await gateway.brain.file(sf.path);
          const { body } = parseFrontmatter(d.content);
          // Extract the first meaningful line (skip # headers, blank lines)
          const lines = body.split('\n').filter(l => l.trim() && !l.startsWith('#'));
          const firstLine = lines[0]?.replace(/^[-*]\s*/, '').trim() || '';
          // Extract date from filename
          const dateMatch = sf.name.match(/^(\d{4}-\d{2}-\d{2})/);
          recentSessions.push({
            path: sf.path,
            name: sf.name.replace('.md', ''),
            date: dateMatch ? dateMatch[1] : '',
            firstLine: firstLine.slice(0, 120),
          });
        } catch { /* */ }
      }

      // Fetch prompt size
      let promptSize = { length: 0, estimatedTokens: 0 };
      try {
        const d = await gateway.brain.prompt();
        promptSize = { length: d.length, estimatedTokens: d.estimatedTokens };
      } catch { /* */ }

      setData({ soul, stats, recentSessions, promptSize });
    }

    load();
  }, [tree]);

  return data;
}

// --- Expandable Content Item ---

function ExpandableItem({ icon, label, labelColor, badge, fetchContent, actions }: {
  icon: React.ReactNode;
  label: string;
  labelColor: string;
  badge?: React.ReactNode;
  fetchContent: () => Promise<string>;
  actions?: React.ReactNode;
}) {
  const [expanded, setExpanded] = useState(false);
  const [content, setContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const toggle = async () => {
    if (!expanded && content === null) {
      setLoading(true);
      try {
        const text = await fetchContent();
        setContent(text);
      } catch (e: any) {
        setContent(`Error: ${e.message}`);
      }
      setLoading(false);
    }
    setExpanded(!expanded);
  };

  // Strip frontmatter for display
  const displayContent = content?.replace(/^---\n[\s\S]*?\n---\n/, '').trim() || '';
  const lines = displayContent.split('\n').length;
  const chars = displayContent.length;

  return (
    <div className="rounded-lg hover:bg-white/[0.02] transition-colors">
      <div className="flex items-center gap-3 px-3 py-2 cursor-pointer group" onClick={toggle}>
        {icon}
        <button onClick={(e) => { e.stopPropagation(); toggle(); }} className={`text-[13px] font-mono ${labelColor} transition-colors text-left truncate`}>
          {expanded ? '▾' : '▸'} {label}
        </button>
        {badge}
        {chars > 0 && expanded && (
          <span className="text-[10px] text-gray-700 font-mono">{lines}L · {(chars / 1024).toFixed(1)}KB</span>
        )}
        <div className="ml-auto flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          {actions}
        </div>
      </div>
      {expanded && (
        <div className="px-3 pb-3">
          {loading ? (
            <div className="text-[11px] text-gray-600 py-2">Loading…</div>
          ) : (
            <div className="rounded-lg border border-[rgb(var(--border))]/40 bg-black/30 p-4 max-h-[400px] overflow-y-auto">
              <MarkdownBlock text={displayContent || '*Empty*'} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DashboardView({ data, liveData, onSelectFile, onRefreshLive }: {
  data: DashboardData;
  liveData: SomaLiveData | null;
  onSelectFile: (path: string) => void;
  onRefreshLive: () => void;
}) {
  const [actionMsg, setActionMsg] = useState<string | null>(null);

  const handleAction = async (action: () => Promise<any>, successMsg: string) => {
    try {
      const result = await action();
      setActionMsg(result.ok ? successMsg : `❌ ${result.error}`);
      onRefreshLive();
      setTimeout(() => setActionMsg(null), 3000);
    } catch (e: any) {
      setActionMsg(`❌ ${e.message}`);
      setTimeout(() => setActionMsg(null), 3000);
    }
  };

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="max-w-3xl mx-auto px-6 py-8 space-y-8">

        {/* Action toast */}
        {actionMsg && (
          <div className="fixed top-4 right-4 z-50 px-4 py-2 rounded-lg bg-gray-800 border border-[rgb(var(--border))]/60 text-sm text-gray-200 shadow-xl animate-in fade-in slide-in-from-top-2">
            {actionMsg}
          </div>
        )}

        {/* Identity */}
        <section>
          <button
            onClick={() => onSelectFile('body/soul.md')}
            className="group w-full text-left"
          >
            <div className="rounded-xl border border-[rgb(var(--border))]/60 bg-gradient-to-br from-gray-900/80 to-gray-950 p-6 transition-colors group-hover:border-purple-500/30">
              <div className="flex items-center gap-2 mb-4">
                <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider text-purple-400 bg-purple-400/10">SOUL</span>
                <span className="text-[11px] text-gray-600 group-hover:text-gray-400 transition-colors">body/soul.md →</span>
                {liveData?.identity?.chain && (
                  <span className="ml-auto text-[10px] text-gray-600 font-mono">
                    chain: {liveData.identity.chain.map(p => p.split('/').slice(-2).join('/')).join(' → ')}
                  </span>
                )}
              </div>
              <div className="text-[14px] text-gray-300 leading-relaxed whitespace-pre-line font-light">
                {data.soul}
              </div>
            </div>
          </button>
        </section>

        {/* Stats Grid */}
        <section className="grid grid-cols-4 gap-3">
          {[
            { label: 'Protocols', count: liveData?.protocols.length ?? data.stats.protocols, icon: Shield, color: 'text-blue-400', hot: liveData?.protocols.filter(p => p.heat >= 8).length ?? 0 },
            { label: 'Muscles', count: liveData?.muscles.length ?? data.stats.muscles, icon: Zap, color: 'text-orange-400', hot: liveData?.muscles.filter(m => m.heat >= 8).length ?? 0 },
            { label: 'Sessions', count: data.stats.sessions, icon: BookOpen, color: 'text-green-400', hot: 0 },
            { label: 'Preloads', count: liveData?.preloads.length ?? data.stats.preloads, icon: ScrollText, color: 'text-yellow-400', hot: liveData?.currentPreload ? 1 : 0 },
          ].map((stat) => (
            <div key={stat.label} className="rounded-lg border border-[rgb(var(--border))]/60 bg-white/[0.04] p-4 text-center">
              <stat.icon className={`h-5 w-5 mx-auto mb-2 ${stat.color}`} />
              <div className="text-2xl font-bold text-white tabular-nums">{stat.count}</div>
              <div className="text-[11px] text-gray-500 uppercase tracking-wider mt-1">{stat.label}</div>
              {stat.hot > 0 && <div className="text-[10px] text-red-400 mt-1">🔥 {stat.hot} hot</div>}
            </div>
          ))}
        </section>

        {/* Heat Overview — compact heatmap of all protocols + muscles */}
        {liveData && (liveData.protocols.length > 0 || liveData.muscles.length > 0) && (
          <section className="rounded-xl border border-[rgb(var(--border))]/60 bg-white/[0.04] p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                <Flame className="h-3.5 w-3.5 text-orange-400" />
                Heat Overview
              </h2>
              <div className="flex items-center gap-3 text-[9px] text-gray-600">
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-red-500" /> HOT 8+</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-amber-500" /> WARM 5-7</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-cyan-500" /> COOL 3-4</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-gray-700" /> COLD 0-2</span>
              </div>
            </div>

            {/* Protocols heatmap */}
            {liveData.protocols.length > 0 && (
              <div className="mb-4">
                <div className="text-[10px] text-blue-400/60 uppercase tracking-wider mb-2 font-semibold">Protocols</div>
                <div className="flex flex-wrap gap-1">
                  {liveData.protocols
                    .sort((a, b) => b.heat - a.heat)
                    .map(p => {
                      const bg = p.heat >= 8 ? 'bg-red-500/80 text-white' : p.heat >= 5 ? 'bg-amber-500/60 text-amber-100' : p.heat >= 3 ? 'bg-cyan-500/40 text-cyan-200' : p.heat > 0 ? 'bg-gray-700/60 text-gray-400' : 'bg-gray-800/40 text-gray-600';
                      return (
                        <button
                          key={p.name}
                          onClick={() => handleAction(() => somaAction('/soma/pin', { name: p.name }), `🔥 Pinned ${p.name}`)}
                          onContextMenu={(e) => { e.preventDefault(); handleAction(() => somaAction('/soma/kill', { name: p.name }), `❄️ Killed ${p.name}`); }}
                          className={`px-2 py-1 rounded text-[10px] font-mono transition-all hover:scale-105 cursor-pointer ${bg}`}
                          title={`${p.name} — heat: ${p.heat}\nClick: pin (+5) | Right-click: kill (→0)`}
                        >
                          {p.name.length > 18 ? p.name.slice(0, 16) + '…' : p.name}
                          {p.heat > 0 && <span className="ml-1 opacity-70">{p.heat}</span>}
                        </button>
                      );
                    })}
                </div>
              </div>
            )}

            {/* Muscles heatmap */}
            {liveData.muscles.length > 0 && (
              <div>
                <div className="text-[10px] text-orange-400/60 uppercase tracking-wider mb-2 font-semibold">Muscles</div>
                <div className="flex flex-wrap gap-1">
                  {liveData.muscles
                    .sort((a, b) => b.heat - a.heat)
                    .map(m => {
                      const bg = m.heat >= 8 ? 'bg-red-500/80 text-white' : m.heat >= 5 ? 'bg-amber-500/60 text-amber-100' : m.heat >= 3 ? 'bg-cyan-500/40 text-cyan-200' : m.heat > 0 ? 'bg-gray-700/60 text-gray-400' : 'bg-gray-800/40 text-gray-600';
                      return (
                        <button
                          key={m.name}
                          onClick={() => handleAction(() => somaAction('/soma/pin', { name: m.name }), `🔥 Pinned ${m.name}`)}
                          onContextMenu={(e) => { e.preventDefault(); handleAction(() => somaAction('/soma/kill', { name: m.name }), `❄️ Killed ${m.name}`); }}
                          className={`px-2 py-1 rounded text-[10px] font-mono transition-all hover:scale-105 cursor-pointer ${bg}`}
                          title={`${m.name} — heat: ${m.heat}\nClick: pin (+5) | Right-click: kill (→0)`}
                        >
                          {m.name.length > 18 ? m.name.slice(0, 16) + '…' : m.name}
                          {m.heat > 0 && <span className="ml-1 opacity-70">{m.heat}</span>}
                        </button>
                      );
                    })}
                </div>
              </div>
            )}

            {liveData.protocols.length === 0 && liveData.muscles.length === 0 && (
              <div className="text-[11px] text-gray-600 text-center py-4">No protocols or muscles discovered</div>
            )}
          </section>
        )}

        {/* Breath Cycle Settings */}
        {liveData?.identity?.settings && (
          <section className="rounded-lg border border-[rgb(var(--border))]/60 bg-white/[0.04] px-5 py-3 flex items-center gap-4 flex-wrap text-[11px]">
            <span className="text-gray-500 uppercase tracking-wider font-semibold">Breath</span>
            <span className={liveData.identity.settings.breathe?.auto ? 'text-emerald-400' : 'text-gray-600'}>
              Auto: {liveData.identity.settings.breathe?.auto ? 'ON' : 'OFF'}
            </span>
            {liveData.identity.settings.breathe?.auto && (
              <>
                <span className="text-gray-500">trigger: {liveData.identity.settings.breathe.triggerAt}%</span>
                <span className="text-gray-500">rotate: {liveData.identity.settings.breathe.rotateAt}%</span>
              </>
            )}
            <span className="text-gray-700">│</span>
            <span className="text-gray-500 uppercase tracking-wider font-semibold">Context</span>
            <span className="text-gray-500">warn: {liveData.identity.settings.context?.warnAt ?? 70}%</span>
            <span className="text-gray-500">urgent: {liveData.identity.settings.context?.urgentAt ?? 80}%</span>
            <span className="text-gray-700">│</span>
            <span className={liveData.identity.settings.keepalive?.enabled !== false ? 'text-pink-400' : 'text-gray-600'}>
              ♥ {liveData.identity.settings.keepalive?.enabled !== false ? 'ON' : 'OFF'}
            </span>
          </section>
        )}

        {/* System Prompt */}
        <section className="rounded-lg border border-[rgb(var(--border))]/60 bg-white/[0.04] px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-[11px] text-gray-500 uppercase tracking-wider">System Prompt</span>
            <span className="text-sm font-mono text-gray-300">
              {(data.promptSize.length / 1024).toFixed(1)}KB
            </span>
          </div>
          <span className="text-sm font-mono text-cyan-400">
            ~{data.promptSize.estimatedTokens.toLocaleString()} tokens
          </span>
        </section>

        {/* Protocols with Heat */}
        {liveData && liveData.protocols.length > 0 && (
          <section>
            <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
              <Shield className="h-3.5 w-3.5 text-blue-400" />
              Protocols
              <span className="text-gray-600 font-normal">({liveData.protocols.length})</span>
            </h2>
            <div className="space-y-1">
              {liveData.protocols
                .sort((a, b) => b.heat - a.heat)
                .map(p => (
                  <div key={p.name} className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/[0.02] transition-colors group">
                    <button
                      onClick={() => onSelectFile(`amps/protocols/${p.name}.md`)}
                      className="text-[13px] font-mono text-gray-300 hover:text-blue-400 transition-colors min-w-[180px] text-left truncate"
                    >
                      {p.name}
                    </button>
                    <HeatBar
                      heat={p.heat}
                      name={p.name}
                      onPin={() => handleAction(() => somaAction('/soma/pin', { name: p.name }), `🔥 Pinned ${p.name}`)}
                      onKill={() => handleAction(() => somaAction('/soma/kill', { name: p.name }), `❄️ Killed ${p.name}`)}
                    />
                    {p.description && (
                      <span className="text-[11px] text-gray-600 truncate ml-auto max-w-[300px]">{p.description}</span>
                    )}
                  </div>
                ))}
            </div>
          </section>
        )}

        {/* Muscles with Heat */}
        {liveData && liveData.muscles.length > 0 && (
          <section>
            <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
              <Zap className="h-3.5 w-3.5 text-orange-400" />
              Muscles
              <span className="text-gray-600 font-normal">({liveData.muscles.length})</span>
            </h2>
            <div className="space-y-1">
              {liveData.muscles
                .sort((a, b) => b.heat - a.heat)
                .map(m => (
                  <div key={m.name} className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/[0.02] transition-colors group">
                    <button
                      onClick={() => onSelectFile(`amps/muscles/${m.name}.md`)}
                      className="text-[13px] font-mono text-gray-300 hover:text-orange-400 transition-colors min-w-[180px] text-left truncate"
                    >
                      {m.name}
                    </button>
                    <HeatBar
                      heat={m.heat}
                      name={m.name}
                      onPin={() => handleAction(() => somaAction('/soma/pin', { name: m.name }), `🔥 Pinned ${m.name}`)}
                      onKill={() => handleAction(() => somaAction('/soma/kill', { name: m.name }), `❄️ Killed ${m.name}`)}
                    />
                    {m.description && (
                      <span className="text-[11px] text-gray-600 truncate ml-auto max-w-[300px]">{m.description}</span>
                    )}
                  </div>
                ))}
            </div>
          </section>
        )}

        {/* MAPs */}
        {liveData && liveData.maps.length > 0 && (
          <section>
            <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
              <Map className="h-3.5 w-3.5 text-cyan-400" />
              MAPs
              <span className="text-gray-600 font-normal">({liveData.maps.length})</span>
            </h2>
            <div className="space-y-1">
              {liveData.maps.map(m => (
                <ExpandableItem
                  key={m.name}
                  icon={<Map className="h-3.5 w-3.5 text-cyan-500/50 shrink-0" />}
                  label={m.name}
                  labelColor="text-gray-300 hover:text-cyan-400"
                  badge={m.status ? <span className="text-[10px] text-gray-600 px-1.5 py-0.5 rounded bg-gray-800">{m.status}</span> : undefined}
                  fetchContent={async () => {
                    const data = await somaApi.map(m.name);
                    return data.content || data.error || 'No content';
                  }}
                  actions={
                    <div className="flex gap-1">
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            // Send MAP activation through chat
                            window.open(`/sessions?msg=${encodeURIComponent(`Read and follow this MAP:\n\n${m.name}`)}`, '_self');
                          } catch { window.open(`/sessions?msg=${encodeURIComponent(`Read and follow this MAP:\n\n${m.name}`)}`, '_self'); }
                        }}
                        className="p-1 rounded hover:bg-green-500/20 text-gray-600 hover:text-green-400 transition-colors"
                        title="Activate MAP (new session)"
                      >
                        <Zap className="h-3 w-3" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); window.open(`/sessions?msg=${encodeURIComponent(`Read and follow this MAP:\n\n${m.name}`)}`, '_self'); }}
                        className="p-1 rounded hover:bg-cyan-500/20 text-gray-600 hover:text-cyan-400 transition-colors"
                        title="Send to chat"
                      >
                        <Play className="h-3 w-3" />
                      </button>
                    </div>
                  }
                />
              ))}
            </div>
          </section>
        )}

        {/* Preloads */}
        {liveData && (
          <section>
            <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
              <ScrollText className="h-3.5 w-3.5 text-yellow-400" />
              Preloads
              <span className="text-gray-600 font-normal">({liveData.preloads.length})</span>
            </h2>
            {liveData.preloads.length === 0 ? (
              <div className="text-[12px] text-gray-600 px-3 py-4">No preloads found. Use <code className="text-gray-400">/breathe</code> or <code className="text-gray-400">/exhale</code> to create one.</div>
            ) : (
              <div className="space-y-1">
                {liveData.preloads.map(p => (
                  <ExpandableItem
                    key={p.path}
                    icon={<ScrollText className="h-3.5 w-3.5 text-yellow-500/50 shrink-0" />}
                    label={p.name}
                    labelColor="text-gray-300"
                    badge={
                      <>
                        {p.stale && <span className="text-[9px] text-amber-500 px-1.5 py-0.5 rounded bg-amber-500/10 font-bold uppercase">Stale</span>}
                        {p.ageDays !== undefined && <span className="text-[10px] text-gray-600">{p.ageDays}d ago</span>}
                      </>
                    }
                    fetchContent={async () => {
                      // Resolve relative path inside .soma/
                      const relPath = p.path.includes('.soma/') ? p.path.split('.soma/')[1] : `memory/preloads/${p.name}`;
                      const data = await gateway.brain.file(relPath);
                      return data.content || data.error || 'No content';
                    }}
                    actions={
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAction(
                            () => somaAction('/soma/inhale', { name: p.name?.replace(/\.md$/, '') }),
                            `🫁 Loaded ${p.name}`
                          );
                        }}
                        className="p-1 rounded hover:bg-cyan-500/20 text-gray-600 hover:text-cyan-400 transition-colors"
                        title="Inhale this preload"
                      >
                        <ArrowDownToLine className="h-3.5 w-3.5" />
                      </button>
                    }
                  />
                ))}
              </div>
            )}
          </section>
        )}

        {/* Health Check */}
        {liveData?.doctor?.checks && (
          <section>
            <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
              <Shield className="w-3 h-3 text-cyan-500/50" />
              Health
              <span className={`text-[10px] px-1.5 py-0.5 rounded ${liveData.doctor.healthy ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`}>
                {liveData.doctor.healthy ? 'OK' : 'Issues'}
              </span>
            </h2>
            <div className="space-y-1">
              {liveData.doctor.checks.map((c, i) => (
                <div key={i} className="flex items-center gap-2 text-[12px] px-2 py-1">
                  <span className={`w-1.5 h-1.5 rounded-full ${c.status === 'ok' ? 'bg-emerald-400' : c.status === 'warn' ? 'bg-amber-400' : 'bg-red-400'}`} />
                  <span className="text-gray-400 min-w-[100px]">{c.name}</span>
                  <span className="text-gray-600 truncate">{c.detail}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Recent Sessions */}
        <section>
          <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Recent Sessions</h2>
          <div className="space-y-2">
            {data.recentSessions.map((session) => (
              <button
                key={session.path}
                onClick={() => onSelectFile(session.path)}
                className="group w-full text-left rounded-lg border border-[rgb(var(--border))]/60 bg-white/[0.04] px-4 py-3 hover:border-green-500/30 transition-colors"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[13px] font-mono text-gray-300 group-hover:text-green-400 transition-colors">
                    {session.name}
                  </span>
                  <span className="text-[11px] text-gray-600">{session.date}</span>
                </div>
                {session.firstLine && (
                  <p className="text-[12px] text-gray-500 truncate">{session.firstLine}</p>
                )}
              </button>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

// --- Main Page ---

export default function BrainPage() {
  const [tree, setTree] = useState<TreeNode[]>([]);
  const [selectedPath, setSelectedPath] = usePersistedState<string | null>('brain:selected', null);
  const [fileData, setFileData] = useState<FileData | null>(null);
  const [loading, setLoading] = useState(false);
  const [expandedDirs, toggleDir] = usePersistedSet('brain:expanded', ['amps', 'amps/muscles', 'amps/protocols', 'body', 'memory', 'memory/sessions', 'memory/preloads']);

  const dashboardData = useDashboardData(tree);
  const { data: somaLive, refresh: refreshSomaLive } = useSomaLiveData();

  const fetchTree = useCallback(async () => {
    try {
      const data = await gateway.brain.tree();
      setTree(data);
    } catch (err) {
      console.error('Failed to fetch tree:', err);
    }
  }, []);

  const fetchFile = useCallback(async (path: string) => {
    setLoading(true);
    try {
      const data = await gateway.brain.file(path);
      setFileData(data);
    } catch (err) {
      console.error('Failed to fetch file:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTree();
  }, [fetchTree]);

  useEffect(() => {
    if (selectedPath) {
      fetchFile(selectedPath);
    }
  }, [selectedPath, fetchFile]);



  const handleSelectFile = (path: string) => {
    setSelectedPath(path);
  };

  const { meta, body } = fileData ? parseFrontmatter(fileData.content) : { meta: {}, body: '' };

  return (
    <div className="flex h-full">
      {/* File Tree */}
      <div className="w-72 shrink-0 border-r border-[rgb(var(--border))]/60 flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[rgb(var(--border))]/60">
          <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">.soma</h2>
          <button
            onClick={fetchTree}
            className="p-1 rounded hover:bg-white/5 text-gray-500 hover:text-gray-300 transition-colors"
            title="Refresh"
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto py-2 px-1">
          {tree.map((node) => (
            <TreeItem
              key={node.path}
              node={node}
              depth={0}
              selectedPath={selectedPath}
              onSelect={handleSelectFile}
              expandedDirs={expandedDirs}
              onToggleDir={toggleDir}
            />
          ))}
        </div>
      </div>

      {/* Right Panel: Dashboard or File Viewer */}
      <div className="flex-1 flex flex-col min-w-0">
        {selectedPath && fileData ? (
          <>
            {/* File Header */}
            <div className="flex items-center gap-3 px-6 py-3 border-b border-[rgb(var(--border))]/60 bg-[rgb(var(--background))]/50">
              <button
                onClick={() => { setSelectedPath(null); setFileData(null); }}
                className="p-1 rounded hover:bg-white/5 text-gray-500 hover:text-gray-300 transition-colors"
                title="Back to overview"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
              <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${getCategoryColor(selectedPath)}`}>
                {getCategoryLabel(selectedPath)}
              </span>
              <span className="text-sm font-mono text-gray-300">{selectedPath}</span>
              <div className="ml-auto flex items-center gap-4 text-[11px] text-gray-600">
                <span>{(fileData.size / 1024).toFixed(1)}KB</span>
                <span>{new Date(fileData.modified).toLocaleDateString()}</span>
              </div>
            </div>

            {/* Frontmatter badges */}
            {Object.keys(meta).length > 0 && (
              <div className="flex flex-wrap gap-2 px-6 py-2 border-b border-[rgb(var(--border))]/40 bg-white/[0.04]">
                {Object.entries(meta).map(([key, value]) => (
                  <span key={key} className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-[rgb(var(--muted))]/50 text-[11px]">
                    <span className="text-gray-500">{key}:</span>
                    <span className="text-gray-300">{value}</span>
                  </span>
                ))}
              </div>
            )}

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {loading ? (
                <div className="text-gray-600 text-sm">Loading...</div>
              ) : (
                <MarkdownBlock text={body} />
              )}
            </div>
          </>
        ) : dashboardData ? (
          <DashboardView data={dashboardData} liveData={somaLive} onSelectFile={handleSelectFile} onRefreshLive={refreshSomaLive} />
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-gray-600 text-sm">Loading...</div>
          </div>
        )}
      </div>
    </div>
  );
}
